/**
 * useRememberedFields - Manages which form fields are remembered across additions.
 *
 * Stores the user's choice of remembered fields in localStorage.
 * This config is global (not per-set or per-folder).
 */
import { useLocalStorage } from '@uidotdev/usehooks';
import { useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

export type RememberedFieldKey =
  | 'folderId'
  | 'condition'
  | 'finish'
  | 'language'
  | 'purchasePrice'
  | 'purchaseCurrency'
  | 'purchaseDate'
  | 'notes'
  | 'isAltered'
  | 'isSigned'
  | 'isPlaytest';

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'mtgcraft:remembered-fields';

const DEFAULT_REMEMBERED: RememberedFieldKey[] = ['folderId', 'purchaseDate'];

/** Ordered list of all configurable fields with display labels. */
export const CONFIGURABLE_FIELDS: { key: RememberedFieldKey; label: string }[] = [
  { key: 'folderId', label: 'Folder' },
  { key: 'condition', label: 'Condition' },
  { key: 'finish', label: 'Finish' },
  { key: 'language', label: 'Language' },
  { key: 'purchasePrice', label: 'Price' },
  { key: 'purchaseCurrency', label: 'Currency' },
  { key: 'purchaseDate', label: 'Date' },
  { key: 'notes', label: 'Notes' },
  { key: 'isAltered', label: 'Altered' },
  { key: 'isSigned', label: 'Signed' },
  { key: 'isPlaytest', label: 'Playtest' },
];

// ============================================================================
// Hook
// ============================================================================

interface UseRememberedFieldsResult {
  rememberedFields: RememberedFieldKey[];
  isRemembered: (key: RememberedFieldKey) => boolean;
  toggleField: (key: RememberedFieldKey) => void;
  resetToDefaults: () => void;
}

export default function useRememberedFields(): UseRememberedFieldsResult {
  const [rememberedFields, setRememberedFields] = useLocalStorage<RememberedFieldKey[]>(STORAGE_KEY, DEFAULT_REMEMBERED);

  const isRemembered = useCallback((key: RememberedFieldKey) => rememberedFields.includes(key), [rememberedFields]);

  const toggleField = useCallback(
    (key: RememberedFieldKey) => {
      setRememberedFields((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    },
    [setRememberedFields],
  );

  const resetToDefaults = useCallback(() => {
    setRememberedFields(DEFAULT_REMEMBERED);
  }, [setRememberedFields]);

  return { rememberedFields, isRemembered, toggleField, resetToDefaults };
}
